// Tenant portal shell — wraps every post-auth consumer route.
//
// Per tenant-shell-strings.md §"Layout decision":
//   212px dark sidebar + main column (52px slim topbar + warm content).
//
// Shell-level states (mirrors admin posture):
//   - normal:        sidebar + topbar + children render
//   - loading:       chrome persists; content shows skeleton
//   - error-401:     chrome persists; content swaps to session-expired panel
//   - error-network: chrome persists; content swaps to network-lost panel
//
// The chrome NEVER blanks during loading/error — only the content slot.

import { useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { Spinner } from '@gc-coach/design-system';
import { useSession } from '../auth/index.js';
import { useI18n } from '../i18n/index.js';
import { env as appEnv } from '../config/env.js';
import { OnboardingGate } from '../onboarding/index.js';
import { TenantSidebar } from './TenantSidebar.js';
import { TenantTopbar } from './TenantTopbar.js';
import type { EnvLabel } from './EnvBadge.js';
import { navItemKeyForPath } from './navModel.js';
import styles from './TenantShell.module.css';

export type ShellState =
  | { kind: 'normal' }
  | { kind: 'loading' }
  | { kind: 'error-401'; onReauth: () => void }
  | { kind: 'error-network'; onRetry: () => void };

export interface TenantShellProps {
  children: ReactNode;
  state?: ShellState;
  /** Override the auto-detected env (from VITE_ENV_LABEL). Useful for tests. */
  envOverride?: EnvLabel;
}

export function TenantShell({
  children,
  state = { kind: 'normal' },
  envOverride,
}: TenantShellProps) {
  const location = useLocation();
  const { refetch } = useSession();
  const envLabel = envOverride ?? resolveEnvLabel(appEnv.envLabel);
  const activeKey = navItemKeyForPath(location.pathname);

  return (
    <div className={styles.shell}>
      <TenantSidebar
        {...(activeKey !== undefined ? { activeKey } : {})}
        env={envLabel}
      />
      <div className={styles.main}>
        <TenantTopbar />
        <main className={styles.content}>
          <ShellStateView state={state}>{children}</ShellStateView>
        </main>
      </div>
      {/* Onboarding gate — null-renders unless the session's
          contractAcceptance.needsAcceptance is true. On completion, refetches
          /auth/me so the session flips and the gate dismisses itself. */}
      <OnboardingGate onComplete={() => void refetch()} />
    </div>
  );
}

function ShellStateView({
  state,
  children,
}: {
  state: ShellState;
  children: ReactNode;
}) {
  const { t } = useI18n();

  switch (state.kind) {
    case 'normal':
      return <>{children}</>;

    case 'loading':
      return (
        <div className={styles.statePanel} role="status" aria-live="polite">
          <Spinner />
          <span className={styles.stateCaption}>{t('shell.stateLoading')}</span>
        </div>
      );

    case 'error-401':
      return (
        <StatePanel
          title={t('shell.state401Title')}
          body={t('shell.state401Body')}
          button={t('shell.state401Button')}
          onClick={state.onReauth}
        />
      );

    case 'error-network':
      return (
        <StatePanel
          title={t('shell.stateNetworkTitle')}
          body={t('shell.stateNetworkBody')}
          button={t('shell.stateNetworkButton')}
          onClick={state.onRetry}
        />
      );
  }
}

function StatePanel({
  title,
  body,
  button,
  onClick,
}: {
  title: string;
  body: string;
  button: string;
  onClick: () => void;
}) {
  return (
    <div className={styles.statePanel} role="alert">
      <h2 className={styles.stateTitle}>{title}</h2>
      <p className={styles.stateBody}>{body}</p>
      <button type="button" className={styles.stateButton} onClick={onClick}>
        {button}
      </button>
    </div>
  );
}

function resolveEnvLabel(raw: string): EnvLabel {
  if (raw === 'prod' || raw === 'staging' || raw === 'dev') return raw;
  return 'dev';
}
